import { useMemo } from 'react';
import { EVENTS } from 'shared/constants';
import uploadService from 'shared/services/upload.service';
import analyticsService from 'src/helpers/analytics.service';

type Asset = {
  assetId: string;
};

type OcrConversionData =
  | { status: 'IN_PROGRESS' | 'FAILED'; results?: undefined }
  | { status: 'DONE' | 'TIMEOUT'; results: unknown };

export function useSmartFormat() {
  return useMemo(() => {
    const convertToSmartFormat = async ({
      knowledgeId,
      knowledgeTitle,
      asset,
      signal,
    }: {
      knowledgeId: string | undefined;
      knowledgeTitle: string | undefined;
      asset: Asset;
      signal: AbortSignal;
    }) => {
      analyticsService.track(EVENTS.SMART_FORMAT_CONVERSION.START, {
        conversionTime: EVENTS.SMART_FORMAT_CONVERSION_TIME.IMMEDIATE,
      });

      try {
        await uploadService.triggerOcr({ assetId: asset.assetId });
      } catch {
        analyticsService.track(EVENTS.SMART_FORMAT_CONVERSION.FAILED, {
          id: knowledgeId,
          title: knowledgeTitle,
          conversionTime: EVENTS.SMART_FORMAT_CONVERSION_TIME.IMMEDIATE,
        });

        return { status: 'FAILED' } as const;
      }

      let pollInterval = 1_000;
      const MAX_POLL_INTERVAL = 3_000;
      const MAX_CONSECUTIVE_ERRORS = 10;
      let consecutiveErrors = 0;

      while (!signal.aborted) {
        const currentInterval = pollInterval;
        pollInterval = Math.min(pollInterval * 2, MAX_POLL_INTERVAL);
        await new Promise(resolve => setTimeout(resolve, currentInterval));

        if (signal.aborted) break;

        try {
          const data = (await uploadService.fetchOcrData({
            assetId: asset.assetId,
          })) as unknown as OcrConversionData;

          consecutiveErrors = 0;

          if (data.status === 'DONE' || data.status === 'TIMEOUT') {
            // TIMEOUT can still carry complete results — the backend may mark a task
            // as TIMEOUT via a cleanup job even after OCR finished successfully.
            analyticsService.track(EVENTS.SMART_FORMAT_CONVERSION.DONE, {
              id: knowledgeId,
              title: knowledgeTitle,
              conversionTime: EVENTS.SMART_FORMAT_CONVERSION_TIME.IMMEDIATE,
            });
            return { status: 'DONE', data: data.results } as const;
          } else if (data.status === 'FAILED') {
            analyticsService.track(EVENTS.SMART_FORMAT_CONVERSION.FAILED, {
              id: knowledgeId,
              title: knowledgeTitle,
              conversionTime: EVENTS.SMART_FORMAT_CONVERSION_TIME.IMMEDIATE,
            });

            return { status: 'FAILED' } as const;
          }
        } catch (e) {
          consecutiveErrors++;
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            analyticsService.track(EVENTS.SMART_FORMAT_CONVERSION.FAILED, {
              id: knowledgeId,
              title: knowledgeTitle,
              conversionTime: EVENTS.SMART_FORMAT_CONVERSION_TIME.IMMEDIATE,
              reason: 'polling_errors',
            });
            return { status: 'FAILED' } as const;
          }
          // Transient error (e.g. token refresh in progress) — keep polling
          continue;
        }
      }

      return { status: 'FAILED' } as const;
    };

    return {
      convertToSmartFormat,
    };
  }, []);
}
